import React, { useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { message } from 'antd';
import { useForm } from 'react-hook-form';
import { useSelector } from 'react-redux';
import { getAutoInsightSettings, saveSettings } from '@src/api/autoInsights';
import { isAdminRoleSelector } from '@store/common/selectors';
import { SettingTypes } from '../Settings';

export function useFetchSettings() {
  return useQuery(['auto-insight-settings'], () => getAutoInsightSettings({}), {
    cacheTime: 0,
    refetchOnWindowFocus: false,
  });
}

export function useSetFormValues({ data, setValue }) {
  useEffect(() => {
    const { threshold = {} }: any = data;

    if (threshold) {
      Object.keys(threshold).forEach((field: any) => {
        setValue(field, threshold[field]);
      });
    }
  }, [data]);
}

export function useFormSave(type) {
  const queryClient = useQueryClient();
  return useMutation((data: any) => saveSettings(data), {
    onSuccess: () => {
      queryClient.invalidateQueries(['auto-insight-settings']);
      queryClient.invalidateQueries(type); // trigger refecth to refresh based on new settings
      message.success(
        'The new settings will take effect within an hour. The results based on old settings will also be part of the list for next 2 days.',
      );
    },
  });
}

const formatFormData = (type, formData) => {
  if (type === SettingTypes.brokenAssests) {
    const formattedFormData = { ...formData };
    const dimensionData = formData['OBJECT_NAME'];
    delete formattedFormData['OBJECT_NAME'];
    formattedFormData['IgnoreDimensions'] = {
      OBJECT_NAME: {
        type: 'contains',
        values: dimensionData || [],
      },
    };
    return formattedFormData;
  }
  return formData;
};

export function useSettingForm({ data, type }) {
  const {
    control,
    handleSubmit,
    formState: { errors },
    setValue,
    reset,
    ...rest
  } = useForm();
  const isAdminUser = useSelector(isAdminRoleSelector);

  useSetFormValues({ data, setValue });
  const mutation = useFormSave(type);

  const onSubmit = (formData) => {
    const formattedFormData = formatFormData(type, formData);
    if (isAdminUser && Object.keys(errors).length === 0) {
      mutation.mutate({
        threshold: formattedFormData,
        id: data.id || null,
        type,
      });
    }
  };

  useEffect(() => {
    return () => reset();
  }, []);

  return {
    ...rest,
    control,
    mutation,
    onSubmit,
    handleSubmit,
    errors,
    readOnly: !isAdminUser,
  };
}
